import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from './Toast';

const QuotationForm = ({ lead, existingQuotation, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [packageData, setPackageData] = useState(null);
  const [autoPopulatedFields, setAutoPopulatedFields] = useState([]);
  const [availableHotels, setAvailableHotels] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(lead?.packageId || lead?.selectedPackage || '');
  const [packageSearchTerm, setPackageSearchTerm] = useState('');
  const [hotelSearchTerms, setHotelSearchTerms] = useState({});

  const [formData, setFormData] = useState({
    // Basic Details - Auto-populated from lead
    customerName: '',
    destination: '',
    startDate: '',
    duration: '',
    adults: '',
    children: '',
    totalPax: '',
    tripId: `TRIP${Date.now()}`,

    // Pricing breakdown
    pricePerAdult: '',
    pricePerChild: '',
    airfarePerPerson: '',
    pricePerPerson: '',
    totalPrice: '',
    packageImage: '',

    // Hotels - Auto-populated from package
    hotels: [],

    // Transportation - Auto-populated from lead/package
    transportation: {
      vehicle: '',
      pickUp: '',
      drop: '',
      schedule: []
    },

    // Itinerary - Auto-populated from package
    itinerary: [],

    // Inclusions & Exclusions - Auto-populated from package
    inclusions: [],
    exclusions: [],

    // Terms - Auto-populated from package
    termsAndConditions: [],

    // Important Notes - Custom editable
    importantNotes: [],

    // Sales Person - Auto-populated from lead
    salesPerson: {
      name: '',
      phone: ''
    },

    // Company Info
    companyInfo: {
      name: 'Gajanan World Tourism',
      address: 'B-9/61 94 SIDDHIVINAYAK LANDMARK , Sangli, Maharashtra, India - 416416'
    }
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [savedItineraries, setSavedItineraries] = useState([]);

  // Fetch package data and populate form
  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        setLoading(true);
        const populatedFields = [];

        console.log('📋 Lead Data:', lead);
        console.log('📝 Existing Quotation:', existingQuotation);

        // If editing existing quotation, pre-fill from quotation data
        if (existingQuotation) {
          console.log('✏️ EDIT MODE: Pre-filling from existing quotation');

          // Extract data from items array or flat structure
          const itemData = existingQuotation.items?.[0] || existingQuotation;

          // Format date properly for input field (YYYY-MM-DD)
          let formattedStartDate = '';
          const rawDate = itemData.travelDates?.startDate || existingQuotation.startDate;
          if (rawDate) {
            try {
              const dateObj = new Date(rawDate);
              if (!isNaN(dateObj.getTime())) {
                // Format as YYYY-MM-DD for date input
                formattedStartDate = dateObj.toISOString().split('T')[0];
              }
            } catch (error) {
              console.error('Date parsing error:', error);
            }
          }
          console.log('🗓️ Edit Mode Date Debug:', { rawDate, formattedStartDate });

          setFormData({
            customerName: existingQuotation.customerName || lead?.customerName || '',
            destination: itemData.destination || existingQuotation.destination || '',
            startDate: formattedStartDate,
            duration: itemData.duration || existingQuotation.duration || { days: '', nights: '' },
            adults: (itemData.adults !== undefined ? itemData.adults : existingQuotation.adults) || '',
            children: (itemData.children !== undefined ? itemData.children : existingQuotation.children !== undefined ? existingQuotation.children : '0'),
            totalPax: itemData.totalPax || existingQuotation.totalPax || '',
            tripId: existingQuotation.tripId || lead?.tourCode || `TRIP${Date.now()}`,

            pricePerAdult: itemData.pricePerAdult || existingQuotation.pricePerAdult || existingQuotation.pricingBreakdown?.adults?.pricePerPerson || '',
            pricePerChild: itemData.pricePerChild || existingQuotation.pricePerChild || existingQuotation.pricingBreakdown?.children?.pricePerPerson || '',
            airfarePerPerson: itemData.airfarePerPerson || existingQuotation.airfarePerPerson || existingQuotation.pricingBreakdown?.adults?.airfare || '',
            pricePerPerson: itemData.pricePerPerson || existingQuotation.pricePerPerson || '',
            totalPrice: itemData.totalPrice || existingQuotation.totalPrice || '',
            packageImage: (itemData.images && itemData.images[0]) || existingQuotation.packageImage || '',
            hotels: (itemData.hotels && itemData.hotels.length > 0 ? itemData.hotels : existingQuotation.hotels) || [],
            transportation: itemData.transportation || existingQuotation.transportation || {
              vehicle: '',
              pickUp: '',
              drop: '',
              schedule: []
            },
            itinerary: (itemData.itinerary && itemData.itinerary.length > 0 ? itemData.itinerary : existingQuotation.itinerary) || [],
            inclusions: (() => { const v = (itemData.inclusions && itemData.inclusions.length > 0 ? itemData.inclusions : existingQuotation.inclusions); if (Array.isArray(v)) return v; if (typeof v === 'string' && v.trim()) return v.split(/\n|,/).map(s => s.trim()).filter(Boolean); return []; })(),
            exclusions: (() => { const v = (itemData.exclusions && itemData.exclusions.length > 0 ? itemData.exclusions : existingQuotation.exclusions); if (Array.isArray(v)) return v; if (typeof v === 'string' && v.trim()) return v.split(/\n|,/).map(s => s.trim()).filter(Boolean); return []; })(),
            termsAndConditions: (() => { const v = (itemData.termsAndConditions && itemData.termsAndConditions.length > 0 ? itemData.termsAndConditions : existingQuotation.termsAndConditions); if (Array.isArray(v)) return v; if (typeof v === 'string' && v.trim()) return v.split('\n').map(s => s.trim()).filter(Boolean); return []; })(),
            importantNotes: (() => { const v = (itemData.importantNotes && itemData.importantNotes.length > 0 ? itemData.importantNotes : existingQuotation.importantNotes); if (Array.isArray(v)) return v; if (typeof v === 'string' && v.trim()) return v.split('\n').map(s => s.trim()).filter(Boolean); return []; })(),
            salesPerson: itemData.salesPerson || existingQuotation.salesPerson || {
              name: lead?.createdByName || 'Sales Team',
              phone: '+91-9027295100'
            },
            companyInfo: itemData.companyInfo || existingQuotation.companyInfo || {}
          });

          setLoading(false);
          return; // Skip package fetching when editing
        }

        // Fetch available hotels and packages from database
        const token = localStorage.getItem('token') ||
          localStorage.getItem('companyToken') ||
          localStorage.getItem('employeeToken');

        // Fetch Hotels
        try {
          const hotelsResponse = await axios.get('/master-data/hotels', {
            headers: { Authorization: `Bearer ${token}` }
          });
          const hotels = hotelsResponse.data?.hotels || hotelsResponse.data || [];
          setAvailableHotels(hotels);
          console.log('🏨 Available Hotels:', hotels);
        } catch (error) {
          console.error('❌ Error fetching hotels:', error);
          console.error('Error details:', error.response?.data || error.message);
        }

        // Fetch Packages
        try {
          const packagesResponse = await axios.get('/tour-packages', {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Handle different response formats
          let packages = [];
          if (Array.isArray(packagesResponse.data)) {
            packages = packagesResponse.data;
          } else if (packagesResponse.data?.packages && Array.isArray(packagesResponse.data.packages)) {
            packages = packagesResponse.data.packages;
          } else if (packagesResponse.data?.data && Array.isArray(packagesResponse.data.data)) {
            packages = packagesResponse.data.data;
          }

          setAvailablePackages(packages);
          console.log('📦 Available Packages:', packages);
        } catch (error) {
          console.error('❌ Error fetching packages:', error);
          console.error('Error details:', error.response?.data || error.message);
          setAvailablePackages([]); // Set empty array on error
        }

        // Fetch saved itineraries for import
        try {
          const itRes = await axios.get('/saved-itinerary', { headers: { Authorization: `Bearer ${token}` } });
          setSavedItineraries(itRes.data.itineraries || []);
        } catch (err) {
          console.error('Error fetching itineraries:', err);
          setSavedItineraries([]);
        }

        // Step 1: Auto-populate from LEAD data first

        // Calculate duration from numberOfNights
        let calculatedDuration = '';
        if (lead?.numberOfNights) {
          const nights = parseInt(lead.numberOfNights);
          const days = nights + 1;
          calculatedDuration = `${nights} Night${nights > 1 ? 's' : ''} / ${days} Day${days > 1 ? 's' : ''}`;
        }

        // Format date properly for input field (YYYY-MM-DD)
        let formattedDate = '';
        const rawDate = lead?.travelDate || lead?.startDate || lead?.checkIn || lead?.checkInDate || lead?.date;
        if (rawDate) {
          try {
            const dateObj = new Date(rawDate);
            if (!isNaN(dateObj.getTime())) {
              // Format as YYYY-MM-DD for date input
              formattedDate = dateObj.toISOString().split('T')[0];
            }
          } catch (error) {
            console.error('Date parsing error:', error);
          }
        }
        console.log('🗓️ Date Debug:', { rawDate, formattedDate });

        const leadData = {
          customerName: lead?.customerName || '',
          destination: lead?.destination || '',
          startDate: formattedDate,
          duration: calculatedDuration || lead?.duration || '',
          adults: lead?.numberOfPax || lead?.adults || lead?.numberOfPeople || '',
          children: lead?.numberOfChildren || lead?.children || '0',
          totalPax: '',
          tripId: lead?.tourCode || `TRIP${Date.now()}`,

          // Pricing breakdown
          pricePerAdult: '',
          pricePerChild: '',
          airfarePerPerson: '',
          pricePerPerson: '',

          // Transportation from lead
          transportation: {
            vehicle: lead?.vehicleType || '',
            pickUp: lead?.pickUp || '',
            drop: lead?.drop || '',
            schedule: []
          },

          // Sales person from lead - exclude email addresses from name
          // Also try to get from localStorage if employee is logged in
          salesPerson: (() => {
            // Try to get employee name from localStorage
            let employeeName = null;
            try {
              const employeeInfo = JSON.parse(localStorage.getItem('employeeInfo') || '{}');
              if (employeeInfo.name && !employeeInfo.name.includes('@')) {
                employeeName = employeeInfo.name;
              }
            } catch (e) { }

            return {
              name: (lead?.createdByName && !lead?.createdByName?.includes('@') ? lead.createdByName : null) ||
                (lead?.salesPersonName && !lead?.salesPersonName?.includes('@') ? lead.salesPersonName : null) ||
                (lead?.assignedToName && !lead?.assignedToName?.includes('@') ? lead.assignedToName : null) ||
                employeeName ||
                'Sales Team',
              phone: lead?.createdByPhone || lead?.salesPersonPhone || '+91-9027295100'
            };
          })()
        };

        // Calculate total pax
        const adultsNum = parseInt(leadData.adults) || 0;
        const childrenNum = parseInt(leadData.children) || 0;
        leadData.totalPax = `${adultsNum + childrenNum} Pax (${adultsNum} Adults${childrenNum > 0 ? `, ${childrenNum} Children` : ''})`;

        if (leadData.customerName) populatedFields.push('Customer Name');
        if (leadData.destination) populatedFields.push('Destination');
        if (leadData.startDate) populatedFields.push('Trip Date');
        if (leadData.duration) populatedFields.push('Duration');
        if (leadData.adults) populatedFields.push('Adults');
        if (leadData.children && leadData.children !== '0') populatedFields.push('Children');
        if (lead?.tourCode) populatedFields.push('Tour Code');
        if (leadData.transportation.vehicle) populatedFields.push('Vehicle Type');
        if (leadData.salesPerson.name) populatedFields.push('Sales Person');

        // Step 2: Fetch PACKAGE data if available
        let packageInfo = {};
        if (lead?.packageId || lead?.selectedPackage) {
          const token = localStorage.getItem('token') ||
            localStorage.getItem('companyToken') ||
            localStorage.getItem('employeeToken');

          const packageId = lead.packageId || lead.selectedPackage;
          console.log('📦 Fetching package:', packageId);

          const response = await axios.get(`/tour-packages/${packageId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const pkg = response.data;
          setPackageData(pkg);
          console.log('✅ Package Data:', pkg);

          packageInfo = {
            packageImage: pkg.coverImage?.url || pkg.coverImage || '',
            duration: pkg.duration || leadData.duration || '',
            pricePerPerson: pkg.pricePerPerson || '',

            // Hotels from package with day-wise mapping
            hotels: pkg.hotels?.map((hotel, index) => ({
              dayNumber: index + 1,
              city: hotel.city || hotel.location || '',
              checkIn: hotel.checkIn || '',
              name: hotel.name || '',
              rating: hotel.rating || 3,
              rooms: hotel.roomType || '4 Deluxe / Similar',
              roomCount: leadData.totalPax || '8 Pax',
              mealPlan: hotel.mealPlan || 'Dinner + Breakfast',
              image: hotel.image?.url || hotel.image || ''
            })) || [],

            // Day-wise Itinerary from package
            itinerary: pkg.itinerary?.map((day, index) => ({
              dayNumber: index + 1,
              date: day.date || '',
              title: day.title || day.name || `Day ${index + 1}`,
              description: day.description || day.activities || '',
              image: day.image?.url || day.image || ''
            })) || [],

            // Inclusions from package
            inclusions: pkg.inclusions || [
              'Ac Accommodation in Above Hotels',
              'Meal as per mention',
              'Hotel Taxes',
              'Transfers and Sightseeing',
              'Toll Taxes, Parking & D.A.'
            ],

            // Exclusions from package
            exclusions: pkg.exclusions || [
              'Airfare',
              'Monuments Fees',
              'Personal Expenses',
              'Anything not mentioned in inclusions'
            ],

            // Transportation schedule from itinerary
            transportation: {
              ...leadData.transportation,
              schedule: pkg.itinerary?.map((day, index) => ({
                day: `Day ${index + 1}`,
                date: day.date || '',
                service: day.title || day.name || ''
              })) || []
            }
          };

          if (packageInfo.packageImage) populatedFields.push('Package Image');
          if (packageInfo.duration) populatedFields.push('Duration');
          if (packageInfo.pricePerPerson) populatedFields.push('Price Per Person');
          if (packageInfo.hotels.length > 0) populatedFields.push(`${packageInfo.hotels.length} Hotels`);
          if (packageInfo.itinerary.length > 0) populatedFields.push(`${packageInfo.itinerary.length} Days Itinerary`);
          if (packageInfo.inclusions.length > 0) populatedFields.push('Inclusions');
          if (packageInfo.exclusions.length > 0) populatedFields.push('Exclusions');
        }

        // Step 3: Merge all data
        setFormData(prev => ({
          ...prev,
          ...leadData,
          ...packageInfo,
          companyInfo: prev.companyInfo,
          termsAndConditions: prev.termsAndConditions
        }));

        setAutoPopulatedFields(populatedFields);
        console.log('✅ Auto-populated fields:', populatedFields);

      } catch (error) {
        console.error('❌ Error fetching data:', error);
        toast.warning('Could not fetch package details. Please fill manually.');
      } finally {
        setLoading(false);
      }
    };

    fetchPackageData();
  }, [lead]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePackageSelection = async (packageId) => {
    if (!packageId) {
      setSelectedPackageId('');
      setPackageData(null);
      return;
    }

    try {
      setLoading(true);
      setSelectedPackageId(packageId);

      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.get(`/tour-packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle different response formats
      const pkg = response.data.package || response.data;
      setPackageData(pkg);
      console.log('✅ Package Selected:', response.data);
      console.log('📦 Actual Package Object:', pkg);
      console.log('📅 Package Itinerary:', pkg.itinerary);

      // Auto-populate ONLY specific fields from package
      let itineraryData = [];

      // Handle different itinerary formats
      if (pkg.itinerary && Array.isArray(pkg.itinerary)) {
        itineraryData = pkg.itinerary.map((day, index) => {
          console.log(`Day ${index + 1} data:`, day);

          // Extract image from various formats
          let imageUrl = '';
          if (day.images && Array.isArray(day.images) && day.images.length > 0) {
            const firstImage = day.images[0];
            imageUrl = typeof firstImage === 'string' ? firstImage : (firstImage?.url || firstImage?.path || '');
          } else if (day.image) {
            imageUrl = typeof day.image === 'string' ? day.image : (day.image?.url || day.image?.path || '');
          } else if (day.imageUrl) {
            imageUrl = day.imageUrl;
          } else if (day.dayImage) {
            imageUrl = typeof day.dayImage === 'string' ? day.dayImage : (day.dayImage?.url || '');
          }

          console.log(`Day ${index + 1} image:`, imageUrl);

          return {
            dayNumber: index + 1,
            date: day.date || '',
            title: day.title || day.name || day.dayTitle || `Day ${index + 1}`,
            description: day.description || day.details || '',
            image: imageUrl,
            category: day.category || 'Sightseeing',
            meals: {
              breakfast: day.meals?.breakfast || false,
              lunch: day.meals?.lunch || false,
              dinner: day.meals?.dinner || false
            },
            transport: day.transport?.mode || day.transport?.details || '',
            from: day.transport?.from || '',
            to: day.transport?.to || '',
            cities: Array.isArray(day.cities) ? day.cities.join(', ') : (day.cities || ''),
            activities: Array.isArray(day.activities) ? day.activities.join(', ') : (typeof day.activities === 'string' ? day.activities : ''),
            sightseeing: Array.isArray(day.sightseeing) ? day.sightseeing.join(', ') : (day.sightseeing || '')
          };
        });
      } else if (pkg.dayWiseItinerary && Array.isArray(pkg.dayWiseItinerary)) {
        itineraryData = pkg.dayWiseItinerary.map((day, index) => {
          let imageUrl = '';
          if (day.images && day.images[0]) {
            imageUrl = typeof day.images[0] === 'string' ? day.images[0] : day.images[0]?.url || '';
          } else if (day.image) {
            imageUrl = typeof day.image === 'string' ? day.image : day.image?.url || '';
          }

          return {
            dayNumber: index + 1,
            date: day.date || '',
            title: day.title || day.name || `Day ${index + 1}`,
            description: day.description || '',
            image: imageUrl,
            category: day.category || 'Sightseeing',
            meals: { breakfast: day.meals?.breakfast || false, lunch: day.meals?.lunch || false, dinner: day.meals?.dinner || false },
            transport: day.transport?.mode || '',
            from: day.transport?.from || '',
            to: day.transport?.to || '',
            cities: Array.isArray(day.cities) ? day.cities.join(', ') : '',
            activities: Array.isArray(day.activities) ? day.activities.join(', ') : '',
            sightseeing: Array.isArray(day.sightseeing) ? day.sightseeing.join(', ') : ''
          };
        });
      }

      console.log('📋 Processed Itinerary:', itineraryData);

      // Extract inclusions (handle different formats)
      let inclusionsData = [];
      console.log('🔍 Checking inclusions in package:', {
        inclusions: pkg.inclusions,
        inclusion: pkg.inclusion,
        packageInclusions: pkg.packageInclusions,
        included: pkg.included,
        includes: pkg.includes,
        whatIncluded: pkg.whatIncluded
      });

      if (Array.isArray(pkg.inclusions) && pkg.inclusions.length > 0) {
        inclusionsData = pkg.inclusions;
      } else if (Array.isArray(pkg.inclusion) && pkg.inclusion.length > 0) {
        inclusionsData = pkg.inclusion;
      } else if (Array.isArray(pkg.packageInclusions) && pkg.packageInclusions.length > 0) {
        inclusionsData = pkg.packageInclusions;
      } else if (Array.isArray(pkg.included) && pkg.included.length > 0) {
        inclusionsData = pkg.included;
      } else if (Array.isArray(pkg.includes) && pkg.includes.length > 0) {
        inclusionsData = pkg.includes;
      } else if (Array.isArray(pkg.whatIncluded) && pkg.whatIncluded.length > 0) {
        inclusionsData = pkg.whatIncluded;
      } else if (typeof pkg.inclusions === 'string' && pkg.inclusions.trim()) {
        // If it's a string, split by newline or comma
        inclusionsData = pkg.inclusions.split(/\n|,/).map(item => item.trim()).filter(Boolean);
      }
      console.log('✓ Inclusions:', inclusionsData);

      // Extract exclusions (handle different formats)
      let exclusionsData = [];
      console.log('🔍 Checking exclusions in package:', {
        exclusions: pkg.exclusions,
        exclusion: pkg.exclusion,
        packageExclusions: pkg.packageExclusions,
        excluded: pkg.excluded,
        excludes: pkg.excludes,
        notIncluded: pkg.notIncluded
      });

      if (Array.isArray(pkg.exclusions) && pkg.exclusions.length > 0) {
        exclusionsData = pkg.exclusions;
      } else if (Array.isArray(pkg.exclusion) && pkg.exclusion.length > 0) {
        exclusionsData = pkg.exclusion;
      } else if (Array.isArray(pkg.packageExclusions) && pkg.packageExclusions.length > 0) {
        exclusionsData = pkg.packageExclusions;
      } else if (Array.isArray(pkg.excluded) && pkg.excluded.length > 0) {
        exclusionsData = pkg.excluded;
      } else if (Array.isArray(pkg.excludes) && pkg.excludes.length > 0) {
        exclusionsData = pkg.excludes;
      } else if (Array.isArray(pkg.notIncluded) && pkg.notIncluded.length > 0) {
        exclusionsData = pkg.notIncluded;
      } else if (typeof pkg.exclusions === 'string' && pkg.exclusions.trim()) {
        // If it's a string, split by newline or comma
        exclusionsData = pkg.exclusions.split(/\n|,/).map(item => item.trim()).filter(Boolean);
      }
      console.log('✗ Exclusions:', exclusionsData);

      // Extract terms and conditions (handle different formats)
      let termsData = [];
      if (Array.isArray(pkg.termsAndConditions)) {
        termsData = pkg.termsAndConditions;
      } else if (typeof pkg.termsAndConditions === 'string' && pkg.termsAndConditions.trim()) {
        // Split by newlines and filter empty lines
        termsData = pkg.termsAndConditions.split('\n').map(t => t.trim()).filter(t => t);
      } else if (Array.isArray(pkg.terms)) {
        termsData = pkg.terms;
      } else if (typeof pkg.terms === 'string' && pkg.terms.trim()) {
        termsData = pkg.terms.split('\n').map(t => t.trim()).filter(t => t);
      } else if (Array.isArray(pkg.termsConditions)) {
        termsData = pkg.termsConditions;
      } else if (typeof pkg.termsConditions === 'string' && pkg.termsConditions.trim()) {
        termsData = pkg.termsConditions.split('\n').map(t => t.trim()).filter(t => t);
      } else if (Array.isArray(pkg.paymentTerms)) {
        termsData = pkg.paymentTerms;
      } else if (pkg.paymentTerms && typeof pkg.paymentTerms === 'object') {
        // Handle paymentTerms object structure
        termsData = Object.values(pkg.paymentTerms).filter(v => v);
      }

      // If no terms from package, keep existing
      if (termsData.length === 0) {
        termsData = formData.termsAndConditions;
      }

      console.log('📋 Terms & Conditions:', termsData);
      console.log('🔍 Package object keys:', Object.keys(pkg));
      console.log('🔍 Looking for terms in:', {
        termsAndConditions: pkg.termsAndConditions,
        terms: pkg.terms,
        termsConditions: pkg.termsConditions,
        paymentTerms: pkg.paymentTerms,
        paymentPolicy: pkg.paymentPolicy,
        bookingTerms: pkg.bookingTerms
      });

      const packageInfo = {
        packageName: pkg.name || pkg.packageName || '',
        packageImage: pkg.coverImage?.url || pkg.coverImage || '',

        // Itinerary from package (Day-wise)
        itinerary: itineraryData,

        // Inclusions from package
        inclusions: inclusionsData,

        // Exclusions from package
        exclusions: exclusionsData,

        // Terms and Conditions from package
        termsAndConditions: termsData
      };

      console.log('📦 Package Info to be set:', packageInfo);

      setFormData(prev => ({
        ...prev,
        ...packageInfo
      }));

      toast.success(`Package "${pkg.name}" loaded!\n📅 ${packageInfo.itinerary.length} Days Itinerary\n✓ ${packageInfo.inclusions.length} Inclusions\n✗ ${packageInfo.exclusions.length} Exclusions`);

    } catch (error) {
      console.error('❌ Error loading package:', error);
      toast.error('Error loading package. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddHotel = () => {
    setFormData(prev => ({
      ...prev,
      hotels: [...prev.hotels, {
        dayNumber: prev.hotels.length + 1,
        city: '',
        checkIn: '',
        name: '',
        rating: 3,
        rooms: '4 Deluxe / Similar',
        roomCount: '8 Pax',
        mealPlan: 'Dinner + Breakfast',
        image: ''
      }]
    }));
  };

  const handleRemoveHotel = (index) => {
    setFormData(prev => ({
      ...prev,
      hotels: prev.hotels.filter((_, i) => i !== index)
    }));
  };

  const handleHotelChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      hotels: prev.hotels.map((hotel, i) =>
        i === index ? { ...hotel, [field]: value } : hotel
      )
    }));
  };

  const handleAddItinerary = () => {
    setFormData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, {
        dayNumber: prev.itinerary.length + 1,
        date: '',
        title: '',
        description: '',
        image: '',
        category: 'Sightseeing',
        meals: { breakfast: false, lunch: false, dinner: false },
        transport: '',
        from: '',
        to: '',
        cities: '',
        activities: '',
        sightseeing: ''
      }]
    }));
  };

  const handleRemoveItinerary = (index) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== index)
    }));
  };

  const handleItineraryChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) =>
        i === index ? { ...day, [field]: value } : day
      )
    }));
  };

  // Handle image upload for package
  const handlePackageImageUpload = async (file) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url || response.data.path || response.data.imageUrl;
      setFormData(prev => ({ ...prev, packageImage: imageUrl }));
      toast.success('Package image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading package image:', error);
      toast.error('Error uploading image. Please try again.');
    }
  };

  // Handle image upload for hotels
  const handleHotelImageUpload = async (index, file) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url || response.data.path || response.data.imageUrl;
      handleHotelChange(index, 'image', imageUrl);
      toast.success('Hotel image uploaded!');
    } catch (error) {
      console.error('Error uploading hotel image:', error);
      toast.error('Error uploading image. Please try again.');
    }
  };

  // Handle image upload for itinerary
  const handleItineraryImageUpload = async (index, file) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      const imageUrl = response.data.url || response.data.path || response.data.imageUrl;
      handleItineraryChange(index, 'image', imageUrl);
      toast.success('Day image uploaded!');
    } catch (error) {
      console.error('Error uploading itinerary image:', error);
      toast.error('Error uploading image. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Calculate total price with breakdown
    const adultsNum = parseInt(formData.adults) || 0;
    const childrenNum = parseInt(formData.children) || 0;
    const totalPax = adultsNum + childrenNum;

    const pricePerAdult = parseFloat(formData.pricePerAdult) || 0;
    const pricePerChild = parseFloat(formData.pricePerChild) || pricePerAdult; // Use adult price if child price not specified
    const airfarePerPerson = parseFloat(formData.airfarePerPerson) || 0;

    // Calculate total
    const adultsTotal = adultsNum * (pricePerAdult + airfarePerPerson);
    const childrenTotal = childrenNum * (pricePerChild + airfarePerPerson);
    const calculatedTotal = adultsTotal + childrenTotal;

    // Calculate price per person (average)
    const calculatedPricePerPerson = totalPax > 0 ? calculatedTotal / totalPax : pricePerAdult + airfarePerPerson;

    // Structure data in the new format with items array
    const finalData = {
      // Keep flat structure for backward compatibility
      ...formData,
      totalPax: `${totalPax} Pax (${adultsNum} Adults${childrenNum > 0 ? `, ${childrenNum} Children` : ''})`,
      pricePerPerson: calculatedPricePerPerson,
      totalPrice: calculatedTotal,   // Always calculated, never stale

      // NEW: Structure data in items array format for proper storage
      items: [{
        destination: formData.destination,
        travelDates: {
          startDate: formData.startDate,
          endDate: formData.endDate
        },
        duration: formData.duration,
        totalPrice: calculatedTotal,
        pricePerPerson: calculatedPricePerPerson,
        pricePerAdult: pricePerAdult,
        pricePerChild: pricePerChild,
        airfarePerPerson: airfarePerPerson,
        adults: adultsNum,
        children: childrenNum,
        totalPax: `${totalPax} Pax (${adultsNum} Adults${childrenNum > 0 ? `, ${childrenNum} Children` : ''})`,

        // Itinerary data — auto-set dates from startDate
        itinerary: (formData.itinerary || []).map((day, i) => {
          if (formData.startDate) {
            const d = new Date(formData.startDate);
            d.setDate(d.getDate() + i);
            const dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
            const dayNum = d.getDate();
            const month = d.toLocaleDateString('en-US', { month: 'long' });
            const year = d.getFullYear();
            const suffix = (dayNum === 1 || dayNum === 21 || dayNum === 31) ? 'st' :
              (dayNum === 2 || dayNum === 22) ? 'nd' : (dayNum === 3 || dayNum === 23) ? 'rd' : 'th';
            return { ...day, date: `${dayOfWeek} ${dayNum}${suffix} ${month} ${year}`, dateRaw: d.toISOString().split('T')[0] };
          }
          return day;
        }),

        // Hotels data
        hotels: formData.hotels || [],

        // Images
        images: formData.packageImage ? [formData.packageImage] : [],

        // Inclusions & Exclusions
        inclusions: formData.inclusions || [],
        exclusions: formData.exclusions || [],
        termsAndConditions: formData.termsAndConditions || [],
        importantNotes: formData.importantNotes || [],

        // Transportation
        transportation: formData.transportation || {},

        // Sales Person
        salesPerson: formData.salesPerson || {},

        // Company Info
        companyInfo: formData.companyInfo || {}
      }],

      // Add pricing breakdown
      pricingBreakdown: {
        adults: {
          count: adultsNum,
          pricePerPerson: pricePerAdult,
          airfare: airfarePerPerson,
          total: adultsTotal
        },
        children: {
          count: childrenNum,
          pricePerPerson: pricePerChild,
          airfare: airfarePerPerson,
          total: childrenTotal
        },
        grandTotal: formData.totalPrice || calculatedTotal
      },

      // Add terms & notes at top level for PDF compatibility
      termsAndConditions: formData.termsAndConditions || [],
      importantNotes: formData.importantNotes || [],
      inclusions: formData.inclusions || [],
      exclusions: formData.exclusions || []
    };

    console.log('📤 Submitting quotation data with items array:', {
      hasItems: !!finalData.items,
      itemsCount: finalData.items?.length,
      hasItinerary: !!finalData.items?.[0]?.itinerary,
      itineraryCount: finalData.items?.[0]?.itinerary?.length,
      hasHotels: !!finalData.items?.[0]?.hotels,
      hotelsCount: finalData.items?.[0]?.hotels?.length,
      destination: finalData.items?.[0]?.destination
    });

    // Save quotation to backend
    try {
      const token = localStorage.getItem('token') ||
        localStorage.getItem('companyToken') ||
        localStorage.getItem('employeeToken');

      let response;

      // Check if this is an edit (existingQuotation has _id) or new quotation
      if (existingQuotation && existingQuotation._id) {
        // UPDATE existing quotation
        console.log('✏️ Updating existing quotation:', existingQuotation._id);
        response = await axios.put(
          `/leads/${lead._id}/quotations/${existingQuotation._id}`,
          finalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // CREATE new quotation
        console.log('➕ Creating new quotation');
        response = await axios.post(
          `/leads/${lead._id}/quotations`,
          finalData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        console.log('✅ Quotation saved to database:', response.data.quotationId || existingQuotation._id);
        console.log('✅ Quotation number:', response.data.quotation?.quotationNumber || existingQuotation.quotationNumber);

        // Pass the saved quotation data with ID to parent
        onSuccess({
          ...finalData,
          _id: response.data.quotationId || existingQuotation._id,
          quotationNumber: response.data.quotation?.quotationNumber || existingQuotation.quotationNumber
        });
      } else {
        toast.error('Error saving quotation: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error saving quotation:', error);
      console.error('❌ Error details:', error.response?.data);
      toast.error('Error saving quotation: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold">Create Quotation</h2>
            <p className="text-sm opacity-90">For {lead?.customerName} - {lead?.destination}</p>
            {packageData && (
              <p className="text-xs opacity-75 mt-1">📦 Package: {packageData.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading details...</p>
            <p className="text-gray-500 text-sm mt-2">Fetching information</p>
          </div>
        ) : (
          <>
            {/* Step Indicator */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between max-w-4xl mx-auto">
                {[
                  { num: 1, label: 'Basic Details' },
                  { num: 2, label: 'Hotels' },
                  { num: 3, label: 'Itinerary' },
                  { num: 4, label: 'Terms' },
                  { num: 5, label: 'Review' }
                ].map((step) => (
                  <div key={step.num} className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${currentStep >= step.num
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                      }`}>
                      {step.num}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${currentStep >= step.num ? 'text-purple-600' : 'text-gray-500'
                      }`}>
                      {step.label}
                    </span>
                    {step.num < 5 && (
                      <div className={`w-12 h-1 mx-3 ${currentStep > step.num ? 'bg-purple-600' : 'bg-gray-300'
                        }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Step 1: Basic Details */}
              {currentStep === 1 && (
                <div className="space-y-6">

                  {/* Import Itinerary */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-2xl">📋</span>
                      <div>
                        <h3 className="text-lg font-bold text-purple-900">Import Saved Itinerary</h3>
                        <p className="text-xs text-purple-700">Select a saved itinerary to auto-fill days, inclusions & exclusions</p>
                      </div>
                    </div>
                    <select
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        try {
                          const token = localStorage.getItem('token') || localStorage.getItem('companyToken') || localStorage.getItem('employeeToken');
                          const res = await axios.get(`/saved-itinerary/${e.target.value}`, { headers: { Authorization: `Bearer ${token}` } });
                          const it = res.data.itinerary;
                          if (it) {
                            setFormData(prev => ({
                              ...prev,
                              destination: it.destination || prev.destination,
                              duration: it.duration ? `${it.duration.nights || 0}N/${it.duration.days || 0}D` : prev.duration,
                              itinerary: (it.itinerary || []).map((day, i) => ({
                                dayNumber: i + 1,
                                date: '', title: day.title || '',
                                description: day.description || '',
                                image: day.images?.[0]?.url || '',
                                category: day.category || 'Sightseeing',
                                meals: day.meals || { breakfast: false, lunch: false, dinner: false },
                                transport: day.transport || '', from: day.from || '', to: day.to || '',
                                cities: day.cities || '', activities: day.activities || ''
                              })),
                              inclusions: it.inclusions?.length ? it.inclusions : prev.inclusions,
                              exclusions: it.exclusions?.length ? it.exclusions : prev.exclusions,
                              termsAndConditions: it.termsAndConditions?.length ? it.termsAndConditions : prev.termsAndConditions
                            }));
                            toast.success(`Itinerary "${it.name}" imported! 📋 ${it.itinerary?.length || 0} days loaded`);
                          }
                        } catch (err) { toast.error('Failed to import itinerary'); }
                        e.target.value = '';
                      }}
                      className="w-full px-4 py-2 border-2 border-purple-400 rounded-lg bg-white text-sm font-medium"
                    >
                      <option value="">-- Select Itinerary to Import --</option>
                      {savedItineraries.map(it => (
                        <option key={it._id} value={it._id}>
                          {it.name} - {it.destination} ({it.duration?.days}D/{it.duration?.nights}N) [{it.itinerary?.length || 0} days]
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Basic Trip Details</h3>
                  </div>

                  {autoPopulatedFields.length > 0 && (
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-green-800 mb-1">
                            ✨ Auto-populated {autoPopulatedFields.length} fields from lead data:
                          </p>
                          <p className="text-xs text-green-700">
                            {autoPopulatedFields.join(' • ')}
                          </p>
                          <p className="text-xs text-green-600 mt-1 italic">
                            All fields are editable. You can modify any information as needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Customer Name *
                      </label>
                      <input
                        type="text"
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Destination *
                      </label>
                      <input
                        type="text"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Trip Date *
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                      {formData.startDate && (
                        <p className="text-xs text-green-600 mt-1">✓ Auto-populated from lead</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration * (e.g., 8 Nights / 9 Days)
                      </label>
                      <input
                        type="text"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        required
                        placeholder="8 Nights / 9 Days"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                      {lead?.numberOfNights && formData.duration && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Auto-calculated from {lead.numberOfNights} night{lead.numberOfNights > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Adults *
                      </label>
                      <input
                        type="number"
                        name="adults"
                        value={formData.adults}
                        onChange={handleChange}
                        required
                        min="1"
                        placeholder="8"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Children
                      </label>
                      <input
                        type="number"
                        name="children"
                        value={formData.children}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Total Pax (Auto-calculated)
                      </label>
                      <input
                        type="text"
                        name="totalPax"
                        value={formData.totalPax || `${(parseInt(formData.adults) || 0) + (parseInt(formData.children) || 0)} Pax`}
                        className="w-full px-4 py-2 border rounded-lg bg-gray-100"
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tour Code
                      </label>
                      <input
                        type="text"
                        name="tripId"
                        value={formData.tripId}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border rounded-lg bg-gray-50 font-mono text-blue-700 font-semibold"
                        readOnly
                        placeholder="Auto-generated from lead"
                      />
                      {lead?.tourCode && (
                        <p className="text-xs text-green-600 mt-1">✓ Auto-populated from lead</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vehicle Type
                      </label>
                      <input
                        type="text"
                        name="vehicle"
                        value={formData.transportation.vehicle}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          transportation: { ...prev.transportation, vehicle: e.target.value }
                        }))}
                        placeholder="1-Ac Tempo/Similar"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pick-Up Location
                      </label>
                      <input
                        type="text"
                        name="pickUp"
                        value={formData.transportation.pickUp}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          transportation: { ...prev.transportation, pickUp: e.target.value }
                        }))}
                        placeholder="Agra"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Drop Location
                      </label>
                      <input
                        type="text"
                        name="drop"
                        value={formData.transportation.drop}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          transportation: { ...prev.transportation, drop: e.target.value }
                        }))}
                        placeholder="Varanasi"
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Package Image Upload Section */}
                    <div className="col-span-2">
                      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                          <span>🖼️</span> Package Cover Image
                        </h4>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Upload Package Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handlePackageImageUpload(file);
                              }
                            }}
                            className="w-full px-4 py-2 border-2 border-dashed border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Upload package cover image (JPG, PNG, max 5MB) - This will appear on the quotation PDF
                          </p>
                          {formData.packageImage && (
                            <div className="mt-3 relative inline-block">
                              <img
                                src={formData.packageImage}
                                alt="Package Cover"
                                className="w-[480px] h-[320px] object-cover rounded-lg border-2 border-green-300 shadow-md"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <p className="text-xs text-red-500 mt-1" style={{ display: 'none' }}>
                                ⚠️ Image failed to load. Please upload again.
                              </p>
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, packageImage: '' }))}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                                title="Remove image"
                              >
                                ✕
                              </button>
                              <p className="text-xs text-green-600 mt-2 font-medium">
                                ✅ Package image loaded successfully!
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Pricing Breakdown Section */}
                    <div className="col-span-2">
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4 mb-4">
                        <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                          <span>💰</span> Pricing Breakdown
                        </h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Price Per Adult (INR) *
                            </label>
                            <input
                              type="number"
                              name="pricePerAdult"
                              value={formData.pricePerAdult}
                              onChange={handleChange}
                              required
                              placeholder="31500"
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Price Per Child (INR)
                            </label>
                            <input
                              type="number"
                              name="pricePerChild"
                              value={formData.pricePerChild}
                              onChange={handleChange}
                              placeholder="20000"
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Leave blank if same as adult</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Airfare Per Person (INR)
                            </label>
                            <input
                              type="number"
                              name="airfarePerPerson"
                              value={formData.airfarePerPerson}
                              onChange={handleChange}
                              placeholder="15000"
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Optional - if airfare included</p>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* ─── Live Price Calculator Preview ─── */}
                  {(() => {
                    const liveAdults = parseInt(formData.adults) || 0;
                    const liveChildren = parseInt(formData.children) || 0;
                    const livePPA = parseFloat(formData.pricePerAdult) || 0;
                    const livePPC = parseFloat(formData.pricePerChild) || livePPA;
                    const liveAirfare = parseFloat(formData.airfarePerPerson) || 0;
                    const liveAdultsTotal = liveAdults * (livePPA + liveAirfare);
                    const liveChildrenTotal = liveChildren * (livePPC + liveAirfare);
                    const liveTotal = liveAdultsTotal + liveChildrenTotal;
                    if (livePPA === 0) return null;
                    return (
                      <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-4">
                        <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                          <span>🧮</span> Live Price Calculator
                        </h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {liveAdults > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-green-200">
                              <p className="text-gray-500 text-xs">Adults ({liveAdults} × ₹{(livePPA + liveAirfare).toLocaleString('en-IN')})</p>
                              <p className="font-bold text-green-700 text-base">₹{liveAdultsTotal.toLocaleString('en-IN')}</p>
                            </div>
                          )}
                          {liveChildren > 0 && (
                            <div className="bg-white rounded-lg p-3 border border-blue-200">
                              <p className="text-gray-500 text-xs">Children ({liveChildren} × ₹{(livePPC + liveAirfare).toLocaleString('en-IN')})</p>
                              <p className="font-bold text-blue-700 text-base">₹{liveChildrenTotal.toLocaleString('en-IN')}</p>
                            </div>
                          )}
                          {liveAirfare > 0 && (
                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 col-span-2">
                              <p className="text-gray-500 text-xs">Airfare (₹{liveAirfare.toLocaleString('en-IN')} × {liveAdults + liveChildren} persons)</p>
                              <p className="font-semibold text-amber-700">₹{(liveAirfare * (liveAdults + liveChildren)).toLocaleString('en-IN')}</p>
                            </div>
                          )}
                        </div>
                        <div className="mt-3 bg-green-700 text-white rounded-lg px-4 py-3 flex justify-between items-center">
                          <span className="font-semibold text-sm">💰 GRAND TOTAL</span>
                          <span className="text-2xl font-black">₹{liveTotal.toLocaleString('en-IN')}</span>
                        </div>
                        <p className="text-xs text-green-600 mt-2 text-center">*GST applicable as per government norms</p>
                      </div>
                    );
                  })()}

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Next: Hotels →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Hotels */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800">Hotels & Accommodations</h3>
                    <button
                      type="button"
                      onClick={handleAddHotel}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      + Add Hotel
                    </button>
                  </div>

                  {formData.hotels.map((hotel, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-bold text-lg">Night {hotel.dayNumber} Hotel</h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveHotel(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️ Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Hotel Selection from Database */}
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Hotel from Database (Optional)
                          </label>

                          {/* Hotel Search */}
                          <input
                            type="text"
                            placeholder="🔍 Search hotels by name or city..."
                            value={hotelSearchTerms[index] || ''}
                            onChange={(e) => setHotelSearchTerms(prev => ({ ...prev, [index]: e.target.value }))}
                            className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm mb-2"
                          />

                          <select
                            onChange={(e) => {
                              const selectedHotel = availableHotels.find(h => h._id === e.target.value);
                              if (selectedHotel) {
                                console.log('🏨 Selected Hotel:', selectedHotel);

                                // Extract image URL from various possible formats
                                let imageUrl = '';
                                if (selectedHotel.images && selectedHotel.images.length > 0) {
                                  const firstImage = selectedHotel.images[0];
                                  if (typeof firstImage === 'string') {
                                    imageUrl = firstImage;
                                  } else if (firstImage?.url) {
                                    imageUrl = firstImage.url;
                                  } else if (firstImage?.path) {
                                    imageUrl = firstImage.path;
                                  }
                                } else if (selectedHotel.image) {
                                  imageUrl = typeof selectedHotel.image === 'string' ? selectedHotel.image : selectedHotel.image?.url || '';
                                }

                                console.log('🖼️ Extracted Image URL:', imageUrl);

                                handleHotelChange(index, 'name', selectedHotel.name);
                                handleHotelChange(index, 'city', selectedHotel.city || selectedHotel.location);
                                handleHotelChange(index, 'rating', selectedHotel.rating || 3);
                                handleHotelChange(index, 'rooms', selectedHotel.roomTypes?.[0] || '');
                                handleHotelChange(index, 'mealPlan', selectedHotel.mealPlans?.[0] || '');
                                handleHotelChange(index, 'image', imageUrl);

                                // Clear search after selection
                                setHotelSearchTerms(prev => ({ ...prev, [index]: '' }));
                              }
                            }}
                            className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-blue-50"
                          >
                            <option value="">-- Select from saved hotels --</option>
                            {availableHotels
                              .filter(h => {
                                const search = (hotelSearchTerms[index] || '').toLowerCase();
                                if (!search) return true;
                                const name = (h.name || '').toLowerCase();
                                const city = (h.city || h.location || '').toLowerCase();
                                return name.includes(search) || city.includes(search);
                              })
                              .map(h => (
                                <option key={h._id} value={h._id}>
                                  {h.name} - {h.city || h.location} ({h.rating || 3}⭐)
                                </option>
                              ))}
                          </select>
                          {hotelSearchTerms[index] && (
                            <p className="text-xs text-blue-600 mt-1">
                              Showing {availableHotels.filter(h => {
                                const search = (hotelSearchTerms[index] || '').toLowerCase();
                                const name = (h.name || '').toLowerCase();
                                const city = (h.city || h.location || '').toLowerCase();
                                return name.includes(search) || city.includes(search);
                              }).length} of {availableHotels.length} hotels
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Or manually enter hotel details below
                          </p>
                        </div>

                        <input
                          type="text"
                          placeholder="City"
                          value={hotel.city}
                          onChange={(e) => handleHotelChange(index, 'city', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          placeholder="Check-in Date"
                          value={hotel.checkIn}
                          onChange={(e) => handleHotelChange(index, 'checkIn', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          placeholder="Hotel Name"
                          value={hotel.name}
                          onChange={(e) => handleHotelChange(index, 'name', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        />
                        <select
                          value={hotel.rating}
                          onChange={(e) => handleHotelChange(index, 'rating', parseInt(e.target.value))}
                          className="px-4 py-2 border rounded-lg"
                        >
                          {[1, 2, 3, 4, 5].map(r => (
                            <option key={r} value={r}>{r} Star</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Room Type"
                          value={hotel.rooms}
                          onChange={(e) => handleHotelChange(index, 'rooms', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          placeholder="Meal Plan"
                          value={hotel.mealPlan}
                          onChange={(e) => handleHotelChange(index, 'mealPlan', e.target.value)}
                          className="px-4 py-2 border rounded-lg"
                        />
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hotel Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleHotelImageUpload(index, file);
                              }
                            }}
                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 hover:border-purple-400 cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Upload hotel image (JPG, PNG, max 5MB)
                          </p>
                          {hotel.image && (
                            <div className="mt-3 relative">
                              <img
                                src={hotel.image}
                                alt={hotel.name || 'Hotel'}
                                className="w-40 h-32 object-cover rounded-lg border-2 border-green-300 shadow-md"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <p className="text-xs text-red-500 mt-1" style={{ display: 'none' }}>
                                ⚠️ Image failed to load.
                              </p>
                              <button
                                type="button"
                                onClick={() => handleHotelChange(index, 'image', '')}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-between gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Next: Itinerary →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Itinerary */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-800">Day-wise Itinerary</h3>
                    <button
                      type="button"
                      onClick={handleAddItinerary}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      + Add Day
                    </button>
                  </div>

                  {formData.itinerary.map((day, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-bold text-lg">Day {day.dayNumber}</h4>
                        <button
                          type="button"
                          onClick={() => handleRemoveItinerary(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          🗑️ Remove
                        </button>
                      </div>

                      <div className="space-y-4">
                        {/* Auto-calculated Date from Trip Date */}
                        <div className="flex gap-2 items-center">
                          {formData.startDate ? (() => {
                            const start = new Date(formData.startDate);
                            start.setDate(start.getDate() + index);
                            const dayOfWeek = start.toLocaleDateString('en-US', { weekday: 'long' });
                            const dayNum = start.getDate();
                            const month = start.toLocaleDateString('en-US', { month: 'long' });
                            const year = start.getFullYear();
                            const suffix = (dayNum === 1 || dayNum === 21 || dayNum === 31) ? 'st' :
                              (dayNum === 2 || dayNum === 22) ? 'nd' :
                                (dayNum === 3 || dayNum === 23) ? 'rd' : 'th';
                            const formattedDate = `${dayOfWeek} ${dayNum}${suffix} ${month} ${year}`;
                            return (
                              <div className="flex-1 px-4 py-2 border-2 border-green-300 rounded-lg bg-green-50 font-medium text-green-800">
                                📅 {formattedDate}
                              </div>
                            );
                          })() : (
                            <div className="flex-1 px-4 py-2 border-2 border-amber-300 rounded-lg bg-amber-50 text-amber-700 text-sm">
                              ⚠️ Set Trip Date in Step 1 to auto-calculate day dates
                            </div>
                          )}
                        </div>
                        <input
                          type="text"
                          placeholder="Title (e.g., Agra Arrival - Transfer And Sightseeing)"
                          value={day.title}
                          onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                          className="w-full px-4 py-2 border rounded-lg"
                        />
                        <textarea
                          placeholder="Description"
                          value={day.description}
                          onChange={(e) => handleItineraryChange(index, 'description', e.target.value)}
                          rows="4"
                          className="w-full px-4 py-2 border rounded-lg"
                        />

                        {/* Category & Meals Row */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">📌 Category</label>
                            <select
                              value={day.category || 'Sightseeing'}
                              onChange={(e) => handleItineraryChange(index, 'category', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                            >
                              <option value="Sightseeing">🏛️ Sightseeing</option>
                              <option value="Adventure">🧗 Adventure</option>
                              <option value="Beach">🏖️ Beach</option>
                              <option value="Shopping">🛍️ Shopping</option>
                              <option value="Cultural">🎭 Cultural</option>
                              <option value="Religious">🛕 Religious</option>
                              <option value="Nature">🌿 Nature</option>
                              <option value="Food & Dining">🍽️ Food & Dining</option>
                              <option value="Leisure">😌 Leisure</option>
                              <option value="Transfer">🚗 Transfer</option>
                              <option value="Free Day">🆓 Free Day</option>
                              <option value="Other">📎 Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">🍽️ Meals Included</label>
                            <div className="flex items-center gap-3 mt-1">
                              <label className="flex items-center gap-1 text-sm cursor-pointer">
                                <input type="checkbox" checked={day.meals?.breakfast || false} onChange={(e) => handleItineraryChange(index, 'meals', { ...day.meals, breakfast: e.target.checked })} className="rounded" />
                                <span>🍳 B</span>
                              </label>
                              <label className="flex items-center gap-1 text-sm cursor-pointer">
                                <input type="checkbox" checked={day.meals?.lunch || false} onChange={(e) => handleItineraryChange(index, 'meals', { ...day.meals, lunch: e.target.checked })} className="rounded" />
                                <span>🍱 L</span>
                              </label>
                              <label className="flex items-center gap-1 text-sm cursor-pointer">
                                <input type="checkbox" checked={day.meals?.dinner || false} onChange={(e) => handleItineraryChange(index, 'meals', { ...day.meals, dinner: e.target.checked })} className="rounded" />
                                <span>🍽️ D</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Transport & Locations */}
                        <div className="grid grid-cols-3 gap-3">
                          <input
                            type="text"
                            placeholder="🚗 Transport (Car, Flight, Train)"
                            value={day.transport || ''}
                            onChange={(e) => handleItineraryChange(index, 'transport', e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="📍 From (City/Location)"
                            value={day.from || ''}
                            onChange={(e) => handleItineraryChange(index, 'from', e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="📍 To (City/Location)"
                            value={day.to || ''}
                            onChange={(e) => handleItineraryChange(index, 'to', e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>

                        {/* Cities & Activities */}
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="text"
                            placeholder="🏙️ Cities (comma separated)"
                            value={day.cities || ''}
                            onChange={(e) => handleItineraryChange(index, 'cities', e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="🎯 Activities / Sightseeing (comma separated)"
                            value={day.activities || day.sightseeing || ''}
                            onChange={(e) => handleItineraryChange(index, 'activities', e.target.value)}
                            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                          />
                        </div>

                        {/* Day Image */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Day Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleItineraryImageUpload(index, file);
                              }
                            }}
                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 hover:border-purple-400 cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Upload day image (JPG, PNG, max 5MB)
                          </p>
                          {day.image && (
                            <div className="mt-3 relative">
                              <img
                                src={day.image}
                                alt={day.title || `Day ${day.dayNumber}`}
                                className="w-40 h-32 object-cover rounded-lg border-2 border-green-300 shadow-md"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <p className="text-xs text-red-500 mt-1" style={{ display: 'none' }}>
                                ⚠️ Image failed to load.
                              </p>
                              <button
                                type="button"
                                onClick={() => handleItineraryChange(index, 'image', '')}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-between gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Next: Terms →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Terms & Conditions */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-800">Inclusions, Exclusions & Terms</h3>

                  {/* Inclusions Section */}
                  <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-lg text-green-900 flex items-center gap-2">
                        <span>✓</span> Package Inclusions
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            inclusions: [...(Array.isArray(prev.inclusions) ? prev.inclusions : []), '']
                          }));
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        + Add Item
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(Array.isArray(formData.inclusions) ? formData.inclusions : []).map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newInclusions = [...(Array.isArray(formData.inclusions) ? formData.inclusions : [])];
                              newInclusions[index] = e.target.value;
                              setFormData(prev => ({ ...prev, inclusions: newInclusions }));
                            }}
                            placeholder="Enter inclusion item"
                            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                inclusions: (Array.isArray(prev.inclusions) ? prev.inclusions : []).filter((_, i) => i !== index)
                              }));
                            }}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Exclusions Section */}
                  <div className="border-2 border-red-300 rounded-lg p-4 bg-red-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-lg text-red-900 flex items-center gap-2">
                        <span>✗</span> Package Exclusions
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            exclusions: [...(Array.isArray(prev.exclusions) ? prev.exclusions : []), '']
                          }));
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        + Add Item
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(Array.isArray(formData.exclusions) ? formData.exclusions : []).map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newExclusions = [...(Array.isArray(formData.exclusions) ? formData.exclusions : [])];
                              newExclusions[index] = e.target.value;
                              setFormData(prev => ({ ...prev, exclusions: newExclusions }));
                            }}
                            placeholder="Enter exclusion item"
                            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                exclusions: (Array.isArray(prev.exclusions) ? prev.exclusions : []).filter((_, i) => i !== index)
                              }));
                            }}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Terms & Conditions Section */}
                  <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-lg text-blue-900 flex items-center gap-2">
                        <span>📋</span> Terms & Conditions
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            termsAndConditions: [...(Array.isArray(prev.termsAndConditions) ? prev.termsAndConditions : []), '']
                          }));
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        + Add Term
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(Array.isArray(formData.termsAndConditions) ? formData.termsAndConditions : []).map((term, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={term}
                            onChange={(e) => {
                              const newTerms = [...(Array.isArray(formData.termsAndConditions) ? formData.termsAndConditions : [])];
                              newTerms[index] = e.target.value;
                              setFormData(prev => ({ ...prev, termsAndConditions: newTerms }));
                            }}
                            placeholder="Enter term or condition"
                            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                termsAndConditions: (Array.isArray(prev.termsAndConditions) ? prev.termsAndConditions : []).filter((_, i) => i !== index)
                              }));
                            }}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Important Notes Section */}
                  <div className="border-2 border-amber-400 rounded-xl p-5 bg-amber-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-bold text-lg text-amber-900 flex items-center gap-2">
                        <span className="text-2xl">⚠️</span>
                        <div>
                          <div>Important Notes</div>
                          <div className="text-xs font-normal text-amber-700">These will appear prominently on the quotation PDF</div>
                        </div>
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            importantNotes: [...(Array.isArray(prev.importantNotes) ? prev.importantNotes : []), '']
                          }));
                        }}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-semibold shadow-sm"
                      >
                        + Add Note
                      </button>
                    </div>

                    {/* Quick Add Preset Notes */}
                    <div className="mb-4 flex flex-wrap gap-2">
                      <p className="text-xs text-amber-700 font-semibold w-full">⚡ Quick Add:</p>
                      {[
                        'Rates are subject to change without prior notice.',
                        'Hotel check-in time is 2:00 PM and check-out is 12:00 PM.',
                        'GST will be charged extra as applicable.',
                        'Airfare/train tickets are not included unless mentioned.',
                        'Package is non-refundable once confirmed.',
                        'Passport must be valid for at least 6 months from travel date.',
                        'Travel insurance is recommended.',
                        'Vehicle will be provided as per the itinerary only (not 24 hrs).',
                      ].map((preset, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            if (!(Array.isArray(formData.importantNotes) ? formData.importantNotes : []).includes(preset)) {
                              setFormData(prev => ({ ...prev, importantNotes: [...(Array.isArray(prev.importantNotes) ? prev.importantNotes : []), preset] }));
                            }
                          }}
                          className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${(Array.isArray(formData.importantNotes) ? formData.importantNotes : []).includes(preset)
                            ? 'bg-amber-400 text-white border-amber-500 cursor-default'
                            : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100 cursor-pointer'
                            }`}
                        >
                          {(Array.isArray(formData.importantNotes) ? formData.importantNotes : []).includes(preset) ? '✓ ' : '+ '}{preset.substring(0, 45)}{preset.length > 45 ? '...' : ''}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-2">
                      {(Array.isArray(formData.importantNotes) ? formData.importantNotes : []).length === 0 ? (
                        <div className="text-center py-6 text-amber-500">
                          <div className="text-3xl mb-2">📝</div>
                          <p className="text-sm">Koi notes nahi hain. Upar "+ Add Note" ya Quick Add use karo.</p>
                        </div>
                      ) : (
                        (Array.isArray(formData.importantNotes) ? formData.importantNotes : []).map((note, index) => (
                          <div key={index} className="flex gap-2 items-start">
                            <span className="mt-2.5 text-amber-500 font-bold text-lg flex-shrink-0">⚠</span>
                            <input
                              type="text"
                              value={note}
                              onChange={(e) => {
                                const newNotes = [...(Array.isArray(formData.importantNotes) ? formData.importantNotes : [])];
                                newNotes[index] = e.target.value;
                                setFormData(prev => ({ ...prev, importantNotes: newNotes }));
                              }}
                              placeholder="Enter important note..."
                              className="flex-1 px-4 py-2.5 border-2 border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 bg-white text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  importantNotes: (Array.isArray(prev.importantNotes) ? prev.importantNotes : []).filter((_, i) => i !== index)
                                }));
                              }}
                              className="mt-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex-shrink-0"
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(5)}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Next: Review →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Review & Submit */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-800">Review & Submit</h3>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg p-6">
                    <h4 className="font-bold text-blue-900 mb-4 text-lg flex items-center gap-2">
                      <span>📋</span> Quotation Summary
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Customer</p>
                        <p className="font-semibold text-gray-900">{formData.customerName}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Destination</p>
                        <p className="font-semibold text-gray-900">{formData.destination}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Duration</p>
                        <p className="font-semibold text-gray-900">{formData.duration}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Travelers</p>
                        <p className="font-semibold text-gray-900">
                          {formData.adults} Adults{formData.children && formData.children !== '0' ? `, ${formData.children} Children` : ''}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Hotels</p>
                        <p className="font-semibold text-green-700">{formData.hotels.length} hotels configured</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Itinerary</p>
                        <p className="font-semibold text-green-700">{formData.itinerary.length} days planned</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Inclusions</p>
                        <p className="font-semibold text-green-700">{formData.inclusions.length} items</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Exclusions</p>
                        <p className="font-semibold text-red-700">{formData.exclusions.length} items</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Terms</p>
                        <p className="font-semibold text-blue-700">{formData.termsAndConditions.length} conditions</p>
                      </div>
                      <div className="bg-amber-50 border border-amber-300 p-3 rounded-lg shadow-sm">
                        <p className="text-amber-600 text-xs mb-1">⚠️ Important Notes</p>
                        <p className="font-semibold text-amber-700">{formData.importantNotes.length} notes</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Per Adult</p>
                        <p className="font-semibold text-purple-700">₹{parseFloat(formData.pricePerAdult || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <p className="text-gray-600 text-xs mb-1">Per Child</p>
                        <p className="font-semibold text-purple-900">₹{parseFloat(formData.pricePerChild || formData.pricePerAdult || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 font-bold text-lg"
                    >
                      ✅ Create Quotation
                    </button>
                  </div>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default QuotationForm;
